import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { TransmissionService } from './transmission.service';

@Injectable({
  providedIn: 'root'
})
export class UpdateCheckService {
  private versionApiUrl = 'app-version';

  constructor(private _api: TransmissionService) { }

  /**
   * Checks if a new app version is available.
   * Returns an object with update info if newer version exists, otherwise null.
   */
  async checkForAppUpdate(): Promise<any | null> {
    try {
      // Get current app version
      const appInfo = await App.getInfo();
      const currentVersion = appInfo.version;
      
      console.log('Current app version:', currentVersion);

      // Fetch latest version from API
      const res: any = await this._api.getSilentPromiseByQuery(this.versionApiUrl)

      console.log('API response:', res);

      if (!res || !res.latestVersion) {
        console.warn('Invalid API response - missing latestVersion');
        return null;
      }

      const latestVersion = res.latestVersion;
      const isNewer = this.isNewerVersion(latestVersion, currentVersion);

      console.log('Version check:', {
        current: currentVersion,
        latest: latestVersion,
        isNewer: isNewer
      });

      if (isNewer) {
        return {
          show: true,
          latestVersion: latestVersion,
          mandatory: res.mandatory || false,
          storeUrl: res.storeUrl || '',
          changelog: res.changelog || null
        };
      }

      return null; // No update available
    } catch (err) {
      console.error('Update check failed:', err);
      return null;
    }
  }

  /**
   * Compares two semantic version strings
   * Returns true if 'latest' is newer than 'current'
   */
  private isNewerVersion(latest: string, current: string): boolean {
    try {
      const latestParts = latest.split('.').map(Number);
      const currentParts = current.split('.').map(Number);

      // Compare each version segment (major.minor.patch)
      const maxLength = Math.max(latestParts.length, currentParts.length);
      
      for (let i = 0; i < maxLength; i++) {
        const latestPart = latestParts[i] || 0;
        const currentPart = currentParts[i] || 0;

        if (latestPart > currentPart) return true;
        if (latestPart < currentPart) return false;
      }

      return false; // Versions are equal
    } catch (err) {
      console.error('Error comparing versions:', err);
      return false;
    }
  }

  /**
   * Manual version check (can be called from settings/menu)
   */
  async manualUpdateCheck(): Promise<{ hasUpdate: boolean; updateInfo?: any }> {
    const updateInfo = await this.checkForAppUpdate();
    return {
      hasUpdate: updateInfo !== null,
      updateInfo: updateInfo
    };
  }
}